import React, { useCallback, useState, useRef, useEffect } from "react";
import { Container, Graphics, Text } from "@pixi/react";
import { CornerNode, SideNode, CenterNode } from "../nodes";
import "pixi.js/events";
import { useApp } from "../../context/AppContext";

const Door = ({ 
  shape, 
  isSelected, 
  onUpdate, 
  onSelect, 
  stageRef, 
  allShapes = [],
  mergedStructures = [],
  isBlueprintMode = false,
  isPartOfMergedStructure = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isScaling, setIsScaling] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [initialShape, setInitialShape] = useState(null);
  const [activeCorner, setActiveCorner] = useState(null);
  const [snapTarget, setSnapTarget] = useState(null);
  const [isNearRoom, setIsNearRoom] = useState(false);
  const [forceOutward, setForceOutward] = useState(false);
  const [showSnapConfirm, setShowSnapConfirm] = useState(false);
  const [snapConfirmData, setSnapConfirmData] = useState(null);
  const containerRef = useRef();
  const isNodeClicked = useRef(false);
  
  const { stage, canvas, shapes } = useApp();

  // Create comprehensive shapes list including merged structures
  const getAllAvailableShapes = useCallback(() => {
    const individualShapes = allShapes || shapes || [];
    const structuresAsShapes = (mergedStructures || []).map(structure => ({
      id: structure.id,
      type: 'mergedStructure',
      shapes: structure.shapes, // Keep the individual shapes for bounds calculation
      x: Math.min(...structure.shapes.map(s => s.x)),
      y: Math.min(...structure.shapes.map(s => s.y)),
      width: Math.max(...structure.shapes.map(s => s.x + (s.width || 0))) - Math.min(...structure.shapes.map(s => s.x)),
      height: Math.max(...structure.shapes.map(s => s.y + (s.height || 0))) - Math.min(...structure.shapes.map(s => s.y))
    }));
    
    return [...individualShapes, ...structuresAsShapes];
  }, [allShapes, shapes, mergedStructures]);

  // Use the comprehensive shapes list
  const safeShapes = getAllAvailableShapes();

  // Add comprehensive debugging for shapes data
  useEffect(() => {
    if (isSelected) {
      console.log('🚪 DOOR SHAPES DEBUG:', {
        doorId: shape?.id,
        shapesFromContext: {
          exists: !!shapes,
          isArray: Array.isArray(shapes),
          length: shapes?.length || 0,
          types: shapes?.map(s => ({ id: s.id, type: s.type })) || []
        },
        allShapesProps: {
          exists: !!allShapes,
          length: allShapes?.length || 0,
          types: allShapes?.map(s => ({ id: s.id, type: s.type })) || []
        },
        mergedStructuresProps: {
          exists: !!mergedStructures,
          length: mergedStructures?.length || 0,
          ids: mergedStructures?.map(s => s.id) || []
        },
        safeShapes: {
          length: safeShapes.length,
          types: safeShapes.map(s => ({ id: s.id, type: s.type }))
        },
        rectangleShapes: safeShapes.filter(s => s.type === 'rectangle' || s.type === 'mergedStructure').length
      });
    }
  }, [isSelected, shapes, allShapes, mergedStructures, safeShapes, shape?.id]);

  const getCoordinates = useCallback((clientX, clientY) => {
    const stage = stageRef.current;
    if (!stage) return null;

    const canvas = stage._canvas;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = stage.app ? stage.app.screen.width / rect.width : 1;
    const scaleY = stage.app ? stage.app.screen.height / rect.height : 1;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  }, [stageRef]);

  // Calculate the actual hinge point based on door rotation
  const getActualHingePoint = useCallback((doorX, doorY, radius, rotation = 0) => {
    // The hinge is at (0, radius) in the door's local coordinate system
    // Transform it to world coordinates based on rotation
    const localHingeX = 0;
    const localHingeY = radius;
    
    // Apply rotation transformation
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    
    const worldHingeX = doorX + (localHingeX * cos - localHingeY * sin);
    const worldHingeY = doorY + (localHingeX * sin + localHingeY * cos);
    
    return { x: worldHingeX, y: worldHingeY };
  }, []);

  // Calculate the bounding box that encompasses all possible rotations of the door
  const getRotationBoundingBox = useCallback((doorX, doorY, radius) => {
    // The door can extend from (0,0) to (radius, radius) in local coords
    // When rotated, it can reach different extents
    const maxExtent = radius * Math.sqrt(2); // Diagonal extent
    
    return {
      x: doorX - maxExtent / 2,
      y: doorY - maxExtent / 2,
      width: maxExtent,
      height: maxExtent
    };
  }, []);

  // Calculate door position needed to place hinge at a specific point
  const getDoorPositionForHingePoint = useCallback((hingeX, hingeY, radius, rotation = 0) => {
    // The hinge is at (0, radius) in the door's local coordinate system
    // We need to find the door position that would place the hinge at (hingeX, hingeY)
    const localHingeX = 0;
    const localHingeY = radius;
    
    // Apply inverse rotation transformation
    const cos = Math.cos(-rotation);
    const sin = Math.sin(-rotation);
    
    // Transform the desired world hinge point back to get the door position
    const doorX = hingeX - (localHingeX * cos - localHingeY * sin);
    const doorY = hingeY - (localHingeX * sin + localHingeY * cos);
    
    return { x: doorX, y: doorY };
  }, []);

  // Get multiple check points on the door for comprehensive snap detection
  const getDoorCheckPoints = useCallback((doorX, doorY, radius, rotation = 0) => {
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    
    // Transform local door points to world coordinates
    const transformPoint = (localX, localY) => ({
      x: doorX + (localX * cos - localY * sin),
      y: doorY + (localX * sin + localY * cos)
    });
    
    return {
      // Main structural points of the quarter-circle door
      hinge: transformPoint(0, radius),           // Bottom-left (hinge point)
      topLeft: transformPoint(0, 0),              // Top-left (wall contact top)
      topRight: transformPoint(radius, 0),        // Top-right (door opening end)
      
      // Edge midpoints for better detection
      leftEdge: transformPoint(0, radius / 2),    // Middle of left edge (wall contact)
      topEdge: transformPoint(radius / 2, 0),     // Middle of top edge
      
      // Additional points along the curved edge
      curveQuarter: transformPoint(radius * 0.7, radius * 0.3),  // Point on arc
      curveHalf: transformPoint(radius * Math.sqrt(2)/2, radius * (1 - Math.sqrt(2)/2)) // Arc midpoint
    };
  }, []);

 // Enhanced snap detection logic focused on Rectangle walls
 const findNearbyRectangleWalls = useCallback((doorX, doorY, doorRadius) => {
  // Add comprehensive safety checks
  if (!safeShapes || safeShapes.length === 0) {
    console.log('🔍 No shapes array available or empty:', { shapes: !!safeShapes, isArray: Array.isArray(safeShapes), length: safeShapes?.length });
    return [];
  }
  
  if (!shape || !shape.id) {
    console.log('🔍 No door shape or shape ID available');
    return [];
  }
  
  const snapDistance = 30; // Snap distance for doors to walls
  
  // Add try-catch for additional safety
  let rectangleShapes = [];
  try {
    rectangleShapes = safeShapes.filter(s => 
      s && s.type && s.id && 
      (s.type === 'rectangle' || s.type === 'mergedStructure') && 
      s.id !== shape.id
    );
  } catch (error) {
    console.error('🔍 Error filtering shapes:', error);
    return [];
  }
  
  console.log('🔍 FINDING WALLS DEBUG:', {
    doorPosition: { x: doorX, y: doorY },
    doorRadius,
    totalShapes: safeShapes.length,
    rectangleShapes: rectangleShapes.length,
    rectangleIds: rectangleShapes.map(s => s?.id).filter(Boolean),
    snapDistance
  });
  
  const nearbyWalls = [];
  
  rectangleShapes.forEach(rectShape => {
    // Safety check for rectShape
    if (!rectShape || !rectShape.type || !rectShape.id) {
      console.warn('🔍 Invalid rectangle shape:', rectShape);
      return;
    }
    
    let bounds = {};
    
    if (rectShape.type === 'rectangle') {
      // Safety check for rectangle properties
      if (typeof rectShape.x !== 'number' || typeof rectShape.y !== 'number' || 
          typeof rectShape.width !== 'number' || typeof rectShape.height !== 'number') {
        console.warn('🔍 Invalid rectangle properties:', rectShape);
        return;
      }
      
      bounds = {
        left: rectShape.x,
        right: rectShape.x + rectShape.width,
        top: rectShape.y,
        bottom: rectShape.y + rectShape.height,
        id: rectShape.id
      };
    } else if (rectShape.type === 'mergedStructure') {
      // Safety check for merged structure
      if (!rectShape.shapes || !Array.isArray(rectShape.shapes) || rectShape.shapes.length === 0) {
        console.warn('🔍 Invalid merged structure:', rectShape);
        return;
      }
      
      // For merged structures, get overall bounding box
      const shapeBounds = rectShape.shapes
        .filter(s => s && typeof s.x === 'number' && typeof s.y === 'number' && 
                    typeof s.width === 'number' && typeof s.height === 'number')
        .map(s => ({
          left: s.x,
          right: s.x + s.width,
          top: s.y,
          bottom: s.y + s.height
        }));
      
      if (shapeBounds.length === 0) {
        console.warn('🔍 No valid shapes in merged structure:', rectShape);
        return;
      }
      
      bounds = {
        left: Math.min(...shapeBounds.map(b => b.left)),
        right: Math.max(...shapeBounds.map(b => b.right)),
        top: Math.min(...shapeBounds.map(b => b.top)),
        bottom: Math.max(...shapeBounds.map(b => b.bottom)),
        id: rectShape.id
      };
    }
    
    // Define the four walls of the rectangle
    const walls = [
      { 
        type: 'left', 
        x: bounds.left, 
        y1: bounds.top, 
        y2: bounds.bottom, 
        normal: { x: -1, y: 0 },
        side: 'left'
      },
      { 
        type: 'right', 
        x: bounds.right, 
        y1: bounds.top, 
        y2: bounds.bottom, 
        normal: { x: 1, y: 0 },
        side: 'right'
      },
      { 
        type: 'top', 
        y: bounds.top, 
        x1: bounds.left, 
        x2: bounds.right, 
        normal: { x: 0, y: -1 },
        side: 'top'
      },
      { 
        type: 'bottom', 
        y: bounds.bottom, 
        x1: bounds.left, 
        x2: bounds.right, 
        normal: { x: 0, y: 1 },
        side: 'bottom'
      }
    ];
    
    walls.forEach(wall => {
      let distance, snapPoint, connectionPoint;
      
      if (wall.type === 'left' || wall.type === 'right') {
        // Vertical walls
        if (doorY >= wall.y1 - snapDistance && doorY <= wall.y2 + snapDistance) {
          distance = Math.abs(doorX - wall.x);
          if (distance < snapDistance) {
            // Clamp door position to wall bounds
            const clampedY = Math.max(wall.y1 + doorRadius, Math.min(wall.y2 - doorRadius, doorY));
            
            // Create two snap options: inward and outward
            const inwardSnapPoint = {
              x: wall.x - doorRadius, // Position door so its right edge (opening side) touches wall from inside
              y: clampedY - doorRadius, // Position so hinge is at proper height
              rotation: wall.normal.x < 0 ? 0 : Math.PI, // Face outward from wall
              orientation: 'inward'
            };
            
            const outwardSnapPoint = {
              x: wall.x, // Position door's left edge (hinge side) at wall for outside opening
              y: clampedY - doorRadius, // Position so hinge is at proper height
              rotation: wall.normal.x < 0 ? Math.PI : 0, // Face inward to wall
              orientation: 'outward'
            };
            
            // Choose the snap point based on door's current position relative to wall OR force outward
            const doorIsInside = (wall.type === 'left' && doorX > wall.x) || (wall.type === 'right' && doorX < wall.x);
            snapPoint = forceOutward ? outwardSnapPoint : (doorIsInside ? inwardSnapPoint : outwardSnapPoint);
            
            connectionPoint = {
              x: wall.x,
              y: clampedY,
              side: wall.type
            };
          }
        }
      } else {
        // Horizontal walls
        if (doorX >= wall.x1 - snapDistance && doorX <= wall.x2 + snapDistance) {
          distance = Math.abs(doorY - wall.y);
          if (distance < snapDistance) {
            // Clamp door position to wall bounds
            const clampedX = Math.max(wall.x1 + doorRadius, Math.min(wall.x2 - doorRadius, doorX));
            
            // Create two snap options: inward and outward
            const inwardSnapPoint = {
              x: clampedX - doorRadius, // Position so door's right edge aligns properly
              y: wall.y - doorRadius, // Position door so its bottom edge (opening side) touches wall from inside
              rotation: wall.normal.y < 0 ? -Math.PI / 2 : Math.PI / 2, // Face outward from wall
              orientation: 'inward'
            };
            
            const outwardSnapPoint = {
              x: clampedX - doorRadius, // Position so door's right edge aligns properly
              y: wall.y, // Position door's top edge (hinge side) at wall for outside opening
              rotation: wall.normal.y < 0 ? Math.PI / 2 : -Math.PI / 2, // Face inward to wall
              orientation: 'outward'
            };
            
            // Choose the snap point based on door's current position relative to wall OR force outward
            const doorIsInside = (wall.type === 'top' && doorY > wall.y) || (wall.type === 'bottom' && doorY < wall.y);
            snapPoint = forceOutward ? outwardSnapPoint : (doorIsInside ? inwardSnapPoint : outwardSnapPoint);
            
            connectionPoint = {
              x: clampedX,
              y: wall.y,
              side: wall.type
            };
          }
        }
      }
      
      if (snapPoint) {
        nearbyWalls.push({
          rectangle: rectShape,
          wall: wall.type,
          snapPoint,
          connectionPoint,
          distance,
          rectangleId: bounds.id,
          rectangleType: rectShape.type,
          wallSide: wall.side
        });
      }
    });
  });
  
  // Return closest snap target
  const sortedWalls = nearbyWalls.sort((a, b) => a.distance - b.distance);
  
  console.log('🔍 WALLS FOUND:', {
    wallsFound: sortedWalls.length,
    walls: sortedWalls.map(w => ({
      rectangleId: w.rectangleId,
      wallSide: w.wallSide,
      distance: Math.round(w.distance)
    }))
  });
  
  return sortedWalls;
}, [safeShapes, shape.id, forceOutward]);

  // Enhanced snap detection that checks multiple door points
  const findNearbyWallsFromMultiplePoints = useCallback((doorX, doorY, radius, rotation = 0) => {
    const checkPoints = getDoorCheckPoints(doorX, doorY, radius, rotation);
    const allNearbyWalls = [];
    
    // Check each point against walls
    Object.entries(checkPoints).forEach(([pointName, point]) => {
      const wallsForPoint = findNearbyRectangleWalls(point.x, point.y, radius);
      wallsForPoint.forEach(wall => {
        // Add the detection point info to the wall data
        wall.detectionPoint = pointName;
        wall.detectionCoords = point;
        allNearbyWalls.push(wall);
      });
    });
    
    // Remove duplicates and sort by distance
    const uniqueWalls = allNearbyWalls.filter((wall, index, arr) => 
      arr.findIndex(w => w.rectangleId === wall.rectangleId && w.wallSide === wall.wallSide) === index
    );
    
    return uniqueWalls.sort((a, b) => a.distance - b.distance);
  }, [getDoorCheckPoints, findNearbyRectangleWalls]);

  // Enhanced alignment detection for horizontal/vertical lines of door
  const findAlignedWalls = useCallback((doorX, doorY, radius, rotation = 0) => {
    const checkPoints = getDoorCheckPoints(doorX, doorY, radius, rotation);
    const alignmentThreshold = 5; // Pixels tolerance for alignment
    const proximityThreshold = 30; // Maximum distance to consider for alignment
    const alignedWalls = [];
    
    // Get all rectangles (individual and merged structures)
    const rectangles = safeShapes.filter(s => 
      s && s.type && (s.type === 'rectangle' || s.type === 'mergedStructure') && s.id !== shape.id
    );
    
    rectangles.forEach(rectShape => {
      let bounds = {};
      
      if (rectShape.type === 'rectangle') {
        bounds = {
          left: rectShape.x,
          right: rectShape.x + rectShape.width,
          top: rectShape.y,
          bottom: rectShape.y + rectShape.height,
          id: rectShape.id,
          type: 'rectangle'
        };
      } else if (rectShape.type === 'mergedStructure') {
        const shapeBounds = rectShape.shapes
          .filter(s => s && typeof s.x === 'number' && typeof s.y === 'number')
          .map(s => ({
            left: s.x,
            right: s.x + s.width,
            top: s.y,
            bottom: s.y + s.height
          }));
        
        if (shapeBounds.length === 0) return;
        
        bounds = {
          left: Math.min(...shapeBounds.map(b => b.left)),
          right: Math.max(...shapeBounds.map(b => b.right)),
          top: Math.min(...shapeBounds.map(b => b.top)),
          bottom: Math.max(...shapeBounds.map(b => b.bottom)),
          id: rectShape.id,
          type: 'mergedStructure'
        };
      }
      
      // Check horizontal line alignments (door's top edge and wall contact edge)
      const horizontalLines = [
        { 
          name: 'topEdge',
          y: checkPoints.topLeft.y,  // Top edge of door
          x1: checkPoints.topLeft.x,
          x2: checkPoints.topRight.x
        },
        { 
          name: 'wallContactEdge',
          y: checkPoints.hinge.y,  // Wall contact edge (hinge level)
          x1: checkPoints.topLeft.x,
          x2: checkPoints.hinge.x
        }
      ];
      
      // Check vertical line alignments (door's left edge and right edge)
      const verticalLines = [
        { 
          name: 'leftEdge',
          x: checkPoints.topLeft.x,  // Left edge of door (wall contact)
          y1: checkPoints.topLeft.y,
          y2: checkPoints.hinge.y
        },
        { 
          name: 'rightEdge',
          x: checkPoints.topRight.x,  // Right edge of door (opening side)
          y1: checkPoints.topRight.y,
          y2: checkPoints.topRight.y + radius // Extend to door bottom
        }
      ];
      
      // Check horizontal line alignments with rectangle edges
      horizontalLines.forEach(doorLine => {
        // Check alignment with rectangle top
        if (Math.abs(doorLine.y - bounds.top) < alignmentThreshold) {
          const distance = Math.abs(doorLine.y - bounds.top);
          const overlapX = Math.max(0, Math.min(doorLine.x2, bounds.right) - Math.max(doorLine.x1, bounds.left));
          
          if (distance < proximityThreshold && overlapX > 10) { // Require some overlap
            alignedWalls.push({
              rectangleId: bounds.id,
              rectangleType: bounds.type,
              rectangle: rectShape,
              wallSide: 'top',
              alignmentType: 'horizontal',
              doorLine: doorLine.name,
              distance: distance,
              snapPoint: {
                x: doorX,
                y: bounds.top - (doorLine.y - doorY), // Adjust door position for alignment
                rotation: rotation,
                orientation: 'aligned'
              },
              connectionPoint: {
                x: (doorLine.x1 + doorLine.x2) / 2,
                y: bounds.top,
                side: 'top'
              }
            });
          }
        }
        
        // Check alignment with rectangle bottom
        if (Math.abs(doorLine.y - bounds.bottom) < alignmentThreshold) {
          const distance = Math.abs(doorLine.y - bounds.bottom);
          const overlapX = Math.max(0, Math.min(doorLine.x2, bounds.right) - Math.max(doorLine.x1, bounds.left));
          
          if (distance < proximityThreshold && overlapX > 10) {
            alignedWalls.push({
              rectangleId: bounds.id,
              rectangleType: bounds.type,
              rectangle: rectShape,
              wallSide: 'bottom',
              alignmentType: 'horizontal',
              doorLine: doorLine.name,
              distance: distance,
              snapPoint: {
                x: doorX,
                y: bounds.bottom - (doorLine.y - doorY),
                rotation: rotation,
                orientation: 'aligned'
              },
              connectionPoint: {
                x: (doorLine.x1 + doorLine.x2) / 2,
                y: bounds.bottom,
                side: 'bottom'
              }
            });
          }
        }
      });
      
      // Check vertical line alignments with rectangle edges
      verticalLines.forEach(doorLine => {
        // Check alignment with rectangle left
        if (Math.abs(doorLine.x - bounds.left) < alignmentThreshold) {
          const distance = Math.abs(doorLine.x - bounds.left);
          const overlapY = Math.max(0, Math.min(doorLine.y2, bounds.bottom) - Math.max(doorLine.y1, bounds.top));
          
          if (distance < proximityThreshold && overlapY > 10) {
            alignedWalls.push({
              rectangleId: bounds.id,
              rectangleType: bounds.type,
              rectangle: rectShape,
              wallSide: 'left',
              alignmentType: 'vertical',
              doorLine: doorLine.name,
              distance: distance,
              snapPoint: {
                x: bounds.left - (doorLine.x - doorX),
                y: doorY,
                rotation: rotation,
                orientation: 'aligned'
              },
              connectionPoint: {
                x: bounds.left,
                y: (doorLine.y1 + doorLine.y2) / 2,
                side: 'left'
              }
            });
          }
        }
        
        // Check alignment with rectangle right
        if (Math.abs(doorLine.x - bounds.right) < alignmentThreshold) {
          const distance = Math.abs(doorLine.x - bounds.right);
          const overlapY = Math.max(0, Math.min(doorLine.y2, bounds.bottom) - Math.max(doorLine.y1, bounds.top));
          
          if (distance < proximityThreshold && overlapY > 10) {
            alignedWalls.push({
              rectangleId: bounds.id,
              rectangleType: bounds.type,
              rectangle: rectShape,
              wallSide: 'right',
              alignmentType: 'vertical',
              doorLine: doorLine.name,
              distance: distance,
              snapPoint: {
                x: bounds.right - (doorLine.x - doorX),
                y: doorY,
                rotation: rotation,
                orientation: 'aligned'
              },
              connectionPoint: {
                x: bounds.right,
                y: (doorLine.y1 + doorLine.y2) / 2,
                side: 'right'
              }
            });
          }
        }
      });
    });
    
    return alignedWalls.sort((a, b) => a.distance - b.distance);
  }, [getDoorCheckPoints, safeShapes, shape.id]);

  const handleMouseDown = useCallback(
    (event) => {
      if (!shape) return;
      
      event.stopPropagation();
      onSelect();

      // Skip individual dragging if part of merged structure
      if (isPartOfMergedStructure) return;

      // Get coordinates properly
      let globalPoint;
      if (event.data?.global) {
        globalPoint = event.data.global;
      } else if (event.clientX !== undefined) {
        globalPoint = getCoordinates(event.clientX, event.clientY);
        if (!globalPoint) return;
      } else {
        return; // Invalid event
      }

      setIsDragging(true);
      setDragStart({
        x: globalPoint.x - shape.x,
        y: globalPoint.y - shape.y,
      });
      
      // Notify merge system of drag start
      window.dispatchEvent(new CustomEvent('shapeDragStart', {
        detail: { shapeId: shape.id }
      }));
      
      window.dispatchEvent(new CustomEvent("propogation-block",{detail:{isBlocked:false}}));
    },
    [shape, onSelect, getCoordinates, isPartOfMergedStructure]
  );

  const handleMouseMove = useCallback(
    (event) => {
      if (!isDragging || !dragStart) return;

      // Get coordinates properly
      let globalPoint;
      if (event.data?.global) {
        globalPoint = event.data.global;
      } else if (event.clientX !== undefined) {
        globalPoint = getCoordinates(event.clientX, event.clientY);
        if (!globalPoint) return;
      } else {
        return; // Invalid event
      }

      const newX = globalPoint.x - dragStart.x;
      const newY = globalPoint.y - dragStart.y;

      onUpdate({ id: shape.id, x: newX, y: newY });
      
      // Notify merge system of drag update
      window.dispatchEvent(new CustomEvent('shapeDragUpdate', {
        detail: { 
          shapeId: shape.id, 
          position: { x: newX, y: newY },
          boundingBox: { x: newX, y: newY, width: (shape.radius || 50) * 2, height: (shape.radius || 50) * 2 }
        }
      }));

      // Check for nearby rectangle walls and snap targets
      const radius = shape.radius || 50;
      const currentRotation = shape.rotation || 0;
      
      // Use both proximity-based and alignment-based detection
      const proximityWalls = findNearbyWallsFromMultiplePoints(newX, newY, radius, currentRotation);
      const alignedWalls = findAlignedWalls(newX, newY, radius, currentRotation);
      
      // Combine and prioritize alignment-based detection
      const allWalls = [...alignedWalls, ...proximityWalls];
      const uniqueWalls = allWalls.filter((wall, index, arr) => 
        arr.findIndex(w => w.rectangleId === wall.rectangleId && w.wallSide === wall.wallSide) === index
      );
      const nearbyWalls = uniqueWalls.sort((a, b) => {
        // Prioritize alignment detections over proximity
        if (a.alignmentType && !b.alignmentType) return -1;
        if (!a.alignmentType && b.alignmentType) return 1;
        return a.distance - b.distance;
      });
      
      console.log('🔍 DOOR MOVE DEBUG:', {
        doorId: shape.id,
        doorPosition: { x: newX, y: newY },
        doorRotation: currentRotation,
        checkPoints: getDoorCheckPoints(newX, newY, radius, currentRotation),
        radius,
        proximityDetections: proximityWalls.length,
        alignmentDetections: alignedWalls.length,
        totalNearbyWalls: nearbyWalls.length,
        wallDetails: nearbyWalls.slice(0, 3).map(w => ({
          side: w.wallSide,
          distance: Math.round(w.distance),
          detectionPoint: w.detectionPoint,
          alignmentType: w.alignmentType,
          doorLine: w.doorLine
        })),
        showSnapConfirm,
        snapConfirmData: !!snapConfirmData,
        allShapes: safeShapes.length,
        rectangleShapes: safeShapes.filter(s => s.type === 'rectangle' || s.type === 'mergedStructure').length,
        dragging: isDragging,
        hasDragStart: !!dragStart
      });
      
      if (nearbyWalls.length > 0) {
        const closestWall = nearbyWalls[0];
        
        // Show snap confirmation popup if not already showing
        if (!showSnapConfirm && !snapConfirmData) {
          // Dispatch external popup request
          window.dispatchEvent(new CustomEvent('showDoorSnapPopup', {
            detail: {
              wall: closestWall,
              doorPosition: { x: newX, y: newY },
              doorId: shape.id
            }
          }));
          
          setSnapConfirmData({
            wall: closestWall,
            doorPosition: { x: newX, y: newY }
          });
          setShowSnapConfirm(true);
          
          console.log('🚪 DOOR NEAR WALL - SHOWING EXTERNAL POPUP:', {
            doorId: shape.id,
            wallSide: closestWall.wallSide,
            rectangleId: closestWall.rectangleId,
            distance: Math.round(closestWall.distance),
            orientation: closestWall.snapPoint.orientation
          });
        }
        
        setSnapTarget(closestWall);
        setIsNearRoom(true);
      } else {
        setSnapTarget(null);
        setIsNearRoom(false);
        // Hide confirmation if door moved away
        if (showSnapConfirm) {
          window.dispatchEvent(new CustomEvent('hideDoorSnapPopup'));
          setShowSnapConfirm(false);
          setSnapConfirmData(null);
        }
      }
    },
    [isDragging, dragStart, onUpdate, getCoordinates, shape, findNearbyWallsFromMultiplePoints, showSnapConfirm, snapConfirmData, safeShapes, findAlignedWalls]
  );

  const handleMouseUp = useCallback(() => {
    // Don't auto-snap if confirmation popup is showing - let user decide
    if (showSnapConfirm || snapConfirmData) {
      // Reset drag state but keep popup visible
      setIsDragging(false);
      setDragStart(null);
      return;
    }
    
    if (snapTarget && isNearRoom) {
      // Apply snap with enhanced connection data (only if no popup)
      const updatedDoor = { 
        x: snapTarget.snapPoint.x,
        y: snapTarget.snapPoint.y,
        rotation: snapTarget.snapPoint.rotation,
        attachedTo: {
          rectangleId: snapTarget.rectangleId,
          rectangleType: snapTarget.rectangleType,
          side: snapTarget.wallSide,
          connectionPoint: snapTarget.connectionPoint,
          offsetX: snapTarget.snapPoint.x - (snapTarget.rectangleType === 'mergedStructure' ? 
            Math.min(...snapTarget.rectangle.shapes.map(s => s.x)) : 
            snapTarget.rectangle.x),
          offsetY: snapTarget.snapPoint.y - (snapTarget.rectangleType === 'mergedStructure' ? 
            Math.min(...snapTarget.rectangle.shapes.map(s => s.y)) : 
            snapTarget.rectangle.y),
          attachedAt: Date.now()
        },
        isAttached: true
      };
      
      onUpdate(updatedDoor);
      
      // Log successful door attachment
      console.log('🚪 DOOR ATTACHED TO ROOM:', {
        doorId: shape.id,
        roomId: snapTarget.rectangleId,
        roomType: snapTarget.rectangleType,
        attachedSide: snapTarget.wallSide,
        position: {
          x: updatedDoor.x,
          y: updatedDoor.y,
          rotation: updatedDoor.rotation
        },
        connectionPoint: snapTarget.connectionPoint,
        timestamp: new Date().toLocaleTimeString()
      });
      
      // Dispatch event for other components to know about door attachment
      window.dispatchEvent(new CustomEvent('doorAttached', {
        detail: {
          doorId: shape.id,
          rectangleId: snapTarget.rectangleId,
          attachmentData: updatedDoor.attachedTo
        }
      }));
    } else if (shape.isAttached) {
      // Door was moved away from room, detach it
      onUpdate({
        attachedTo: null,
        isAttached: false
      });
      
      console.log('🚪 DOOR DETACHED FROM ROOM:', {
        doorId: shape.id,
        previouslyAttachedTo: shape.attachedTo?.rectangleId,
        timestamp: new Date().toLocaleTimeString()
      });
      
      // Dispatch detachment event
      window.dispatchEvent(new CustomEvent('doorDetached', {
        detail: {
          doorId: shape.id,
          previousAttachment: shape.attachedTo
        }
      }));
    }
    
    setIsDragging(false);
    setDragStart(null);
    setSnapTarget(null);
    setIsNearRoom(false);
    
    // Notify merge system of drag end
    if (shape) {
      window.dispatchEvent(new CustomEvent('shapeDragEnd', {
        detail: { shapeId: shape.id }
      }));
    }
  }, [shape, snapTarget, isNearRoom, onUpdate, showSnapConfirm, snapConfirmData]);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (event) => {
        if (!dragStart) return;

        const globalPoint = getCoordinates(event.clientX, event.clientY);
        if (!globalPoint) return;

        const newX = globalPoint.x - dragStart.x;
        const newY = globalPoint.y - dragStart.y;
        
        // Just update position - let the main handleMouseMove handle snap detection
        onUpdate({ x: newX, y: newY });
      };

      const handleGlobalMouseUp = () => {
        if (snapTarget && isNearRoom) {
          // Apply snap
          onUpdate({ 
            x: snapTarget.snapPoint.x,
            y: snapTarget.snapPoint.y,
            rotation: snapTarget.snapPoint.rotation,
            snappedToRoom: snapTarget.rectangleId
          });
        }
        
        setIsDragging(false);
        setDragStart(null);
        setSnapTarget(null);
        setIsNearRoom(false);
      };

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart, onUpdate, getCoordinates, findNearbyWallsFromMultiplePoints, snapTarget, isNearRoom, shape]);

  // Handle node interactions for scaling and rotation
  const handleNodeMouseDown = useCallback(
    (event, nodeIndex) => {
      event.stopPropagation();
      isNodeClicked.current = true;

      let globalPoint;
      if (event.data?.global) {
        globalPoint = event.data.global;
      } else if (event.clientX !== undefined) {
        globalPoint = getCoordinates(event.clientX, event.clientY);
        if (!globalPoint) return;
      } else {
        return;
      }

      if (nodeIndex === 'rotation') {
        setIsRotating(true);
      } else if (nodeIndex === 'bottom-line') {
        setIsScaling(true);
        setActiveCorner('bottom-line');
      } else {
        setIsScaling(true);
        setActiveCorner(nodeIndex);
      }
      
      setDragStart({ x: globalPoint.x, y: globalPoint.y });
      setInitialShape({ ...shape });

      window.dispatchEvent(new CustomEvent("propogation-block", { detail: { isBlocked: true } }));
    },
    [shape, getCoordinates]
  );

  const handleNodeMouseMove = useCallback(
    (event, globalPoint) => {
      if (!dragStart || !initialShape) return;

      if (isRotating) {
        // Calculate rotation angle
        const centerX = shape.x + (shape.radius || 50);
        const centerY = shape.y + (shape.radius || 50) / 2;
        
        const angle = Math.atan2(
          globalPoint.y - centerY,
          globalPoint.x - centerX
        );
        
        onUpdate({ rotation: angle });
      } else if (isScaling && activeCorner !== null) {
        // Calculate deltas from the corner's original position to the new mouse position
        const deltaX = globalPoint.x - dragStart.x;
        const deltaY = globalPoint.y - dragStart.y;

        let updates = {};
        
        if (activeCorner === 'bottom-line') {
          // Bottom line scaling - adjust radius based on horizontal movement
          const newRadius = Math.max(20, Math.abs(globalPoint.x - (initialShape.x + initialShape.radius)));
          updates = {
            radius: newRadius
          };
        } else {
          // Regular corner scaling
          switch (activeCorner) {
            case 0: // top-left (affects radius and position)
              const newRadius = Math.max(10, initialShape.radius - deltaX);
              updates = {
                radius: newRadius,
                x: globalPoint.x,
                y: globalPoint.y
              };
              break;
            case 1: // top-right (affects radius)
              updates = {
                radius: Math.max(10, globalPoint.x - initialShape.x)
              };
              break;
            case 2: // bottom-left (affects radius and position)
              const newRadiusLeft = Math.max(10, initialShape.radius - deltaX);
              updates = {
                radius: newRadiusLeft,
                x: globalPoint.x,
                y: initialShape.y
              };
              break;
          }
        }

        onUpdate(updates);
      }
    },
    [isRotating, isScaling, dragStart, initialShape, activeCorner, onUpdate, shape]
  );

  const handleNodeMouseUp = useCallback(() => {
    setIsScaling(false);
    setIsRotating(false);
    setActiveCorner(null);
    setDragStart(null);
    setInitialShape(null);
    isNodeClicked.current = false;

    window.dispatchEvent(new CustomEvent("propogation-block", { detail: { isBlocked: false } }));
  }, []);

  // Add global mouse event listeners for scaling and rotation
  useEffect(() => {
    if (isScaling || isRotating) {
      const handleGlobalMouseMove = (event) => {
        const globalPoint = getCoordinates(event.clientX, event.clientY);
        if (!globalPoint) return;
        handleNodeMouseMove(event, globalPoint);
      };

      const handleGlobalMouseUp = () => {
        handleNodeMouseUp();
      };

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isScaling, isRotating, handleNodeMouseMove, handleNodeMouseUp, getCoordinates]);

  // Handle keyboard events for door orientation toggle
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isSelected && event.key.toLowerCase() === 'f') {
        setForceOutward(prev => !prev);
        console.log('🚪 DOOR ORIENTATION TOGGLED:', forceOutward ? 'Inward' : 'Outward');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSelected, forceOutward]);

  // Listen for external popup responses
  useEffect(() => {
    const handleDoorSnapResponse = (event) => {
      const { confirmed, snapData } = event.detail;
      
      if (snapData.doorId !== shape.id) return; // Not for this door
      
      console.log('🚪 RECEIVED SNAP RESPONSE:', { confirmed, snapData });
      
      if (confirmed && snapData.wall) {
        // User confirmed - apply the snap with rotation-aware positioning
        const wall = snapData.wall;
        const currentRotation = shape.rotation || 0;
        const radius = shape.radius || 50;
        
        // Calculate where the door should be positioned to place its hinge at the wall
        // Use the connection point as the target hinge location
        const targetHingePoint = wall.connectionPoint;
        const doorPosition = getDoorPositionForHingePoint(
          targetHingePoint.x, 
          targetHingePoint.y, 
          radius, 
          currentRotation
        );
        
        const updatedDoor = { 
          x: doorPosition.x,
          y: doorPosition.y,
          rotation: currentRotation, // Maintain current rotation
          attachedTo: {
            rectangleId: wall.rectangleId,
            rectangleType: wall.rectangleType,
            side: wall.wallSide,
            connectionPoint: wall.connectionPoint,
            offsetX: doorPosition.x - (wall.rectangleType === 'mergedStructure' ? 
              Math.min(...wall.rectangle.shapes.map(s => s.x)) : 
              wall.rectangle.x),
            offsetY: doorPosition.y - (wall.rectangleType === 'mergedStructure' ? 
              Math.min(...wall.rectangle.shapes.map(s => s.y)) : 
              wall.rectangle.y),
            attachedAt: Date.now()
          },
          isAttached: true
        };
        
        onUpdate(updatedDoor);
        
        console.log('🚪 DOOR ATTACHED WITH ROTATION-AWARE POSITIONING:', {
          doorId: shape.id,
          rectangleId: wall.rectangleId,
          doorRotation: currentRotation,
          doorPosition: doorPosition,
          targetHingePoint: targetHingePoint,
          offsetX: updatedDoor.attachedTo.offsetX,
          offsetY: updatedDoor.attachedTo.offsetY,
          wallSide: wall.wallSide
        });
        
        // Dispatch event for other components
        window.dispatchEvent(new CustomEvent('doorAttached', {
          detail: {
            doorId: shape.id,
            rectangleId: wall.rectangleId,
            attachmentData: updatedDoor.attachedTo
          }
        }));
      } else {
        // User declined - just update position without snapping
        if (snapData.doorPosition) {
          onUpdate({ 
            x: snapData.doorPosition.x, 
            y: snapData.doorPosition.y 
          });
        }
      }
      
      // Reset confirmation state
      setShowSnapConfirm(false);
      setSnapConfirmData(null);
      setSnapTarget(null);
      setIsNearRoom(false);
    };

    window.addEventListener('doorSnapResponse', handleDoorSnapResponse);
    return () => window.removeEventListener('doorSnapResponse', handleDoorSnapResponse);
  }, [shape.id, onUpdate]);

  // Listen for rectangle movement to update attached door position
  useEffect(() => {
    if (!shape.isAttached || !shape.attachedTo) return;

    const handleRectangleMove = (event) => {
      const { shapeId, position, boundingBox } = event.detail;
      
      // Check if this is the rectangle our door is attached to
      if (shapeId === shape.attachedTo.rectangleId) {
        console.log('🏠 ATTACHED RECTANGLE MOVED:', {
          rectangleId: shapeId,
          newPosition: position,
          doorId: shape.id,
          currentOffset: { x: shape.attachedTo.offsetX, y: shape.attachedTo.offsetY }
        });
        
        // Calculate new door position based on rectangle movement and stored offset
        const newDoorPosition = {
          x: position.x + shape.attachedTo.offsetX,
          y: position.y + shape.attachedTo.offsetY
        };
        
        // Update door position to follow the rectangle
        onUpdate({
          x: newDoorPosition.x,
          y: newDoorPosition.y
        });
        
        console.log('🚪 DOOR POSITION UPDATED TO FOLLOW RECTANGLE:', {
          doorId: shape.id,
          newPosition: newDoorPosition,
          rectanglePosition: position
        });
      }
    };

    // Listen for both individual shape updates and merged structure updates
    window.addEventListener('shapeDragUpdate', handleRectangleMove);
    window.addEventListener('mergedStructureMove', handleRectangleMove);

    return () => {
      window.removeEventListener('shapeDragUpdate', handleRectangleMove);
      window.removeEventListener('mergedStructureMove', handleRectangleMove);
    };
  }, [shape.isAttached, shape.attachedTo, shape.id, onUpdate]);

  // Listen for merge/unmerge events to preserve door attachments
  useEffect(() => {
    if (!shape.isAttached || !shape.attachedTo) return;

    const handleMergeEvent = (event) => {
      const { mergedStructureId, shapesInvolved } = event.detail;
      
      // Check if our attached rectangle is involved in the merge
      const isAttachedRectangleInvolved = shapesInvolved.some(s => s.id === shape.attachedTo.rectangleId);
      
      if (isAttachedRectangleInvolved) {
        console.log('🔄 RECTANGLE MERGED - UPDATING DOOR ATTACHMENT:', {
          doorId: shape.id,
          oldRectangleId: shape.attachedTo.rectangleId,
          newMergedStructureId: mergedStructureId,
          shapesInvolved: shapesInvolved.map(s => s.id)
        });
        
        // Update attachment to point to the new merged structure
        const updatedAttachment = {
          ...shape.attachedTo,
          rectangleId: mergedStructureId,
          rectangleType: 'mergedStructure',
          mergedAt: Date.now()
        };
        
        onUpdate({
          attachedTo: updatedAttachment
        });
        
        console.log('🚪 DOOR ATTACHMENT UPDATED FOR MERGE:', {
          doorId: shape.id,
          newAttachment: updatedAttachment
        });
      }
    };

    const handleUnmergeEvent = (event) => {
      const { originalShapes, unmergedStructureId } = event.detail;
      
      // Check if our door was attached to the unmerged structure
      if (shape.attachedTo.rectangleId === unmergedStructureId) {
        console.log('🔄 STRUCTURE UNMERGED - UPDATING DOOR ATTACHMENT:', {
          doorId: shape.id,
          oldStructureId: unmergedStructureId,
          originalShapes: originalShapes.map(s => s.id)
        });
        
        // Find the closest original rectangle to maintain attachment
        // For simplicity, attach to the first rectangle (could be enhanced)
        if (originalShapes.length > 0) {
          const newTargetRectangle = originalShapes[0];
          
          const updatedAttachment = {
            ...shape.attachedTo,
            rectangleId: newTargetRectangle.id,
            rectangleType: 'rectangle',
            unmergedAt: Date.now()
          };
          
          onUpdate({
            attachedTo: updatedAttachment
          });
          
          console.log('🚪 DOOR ATTACHMENT UPDATED FOR UNMERGE:', {
            doorId: shape.id,
            newTargetRectangle: newTargetRectangle.id,
            newAttachment: updatedAttachment
          });
        }
      }
    };

    // Listen for merge and unmerge events
    window.addEventListener('rectanglesMerged', handleMergeEvent);
    window.addEventListener('structureUnmerged', handleUnmergeEvent);

    return () => {
      window.removeEventListener('rectanglesMerged', handleMergeEvent);
      window.removeEventListener('structureUnmerged', handleUnmergeEvent);
    };
  }, [shape.isAttached, shape.attachedTo, shape.id, onUpdate]);

  if (!shape) return null;

  const radius = shape.radius || 50;

  return (
    <Container
      ref={containerRef}
      x={0}
      y={0}
      interactive={true}
      pointerdown={handleMouseDown}
    >
      {/* Invisible hit area for door dragging - rotation-aware */}
      <Graphics
        cursor={"pointer"}
        draw={(g) => {
          g.clear();
          g.lineStyle(0);
          // Use shape alpha for preview mode, default to very low alpha for hit detection
          const hitAlpha = shape.isPreview ? 0 : 0.01; // No hit area for preview
          g.beginFill(0x000000, hitAlpha);
          
          // Create a hit area that encompasses all possible rotations
          const rotationBounds = getRotationBoundingBox(shape.x, shape.y, radius);
          g.drawRect(rotationBounds.x, rotationBounds.y, rotationBounds.width, rotationBounds.height);
          g.endFill();
        }}
        interactive={true}
        pointerdown={handleMouseDown}
        click={handleMouseDown}
        mousedown={handleMouseDown}
        pointermove={isDragging ? handleMouseMove : undefined}
        pointerup={handleMouseUp}
      />

      {/* Main door shape */}
      <Container
        x={shape.x}
        y={shape.y}
        rotation={shape.rotation || 0}
      >
        <Graphics
          x={0}
          y={0}
          draw={(g) => {
            if (!shape) return;
            
            g.clear();
            
            // Enhanced color coding based on state
            let strokeColor, fillColor, strokeWidth;
            
            if (isBlueprintMode) {
              // Blueprint mode - all white
              strokeColor = 0xffffff;
              fillColor = 0xffffff;
              strokeWidth = isSelected ? 3 : 2;
            } else if (isNearRoom && snapTarget) {
              // Snap preview state - bright green
              strokeColor = 0x00ff00;
              fillColor = 0x00ff88;
              strokeWidth = 4;
            } else if (shape.isAttached) {
              // Attached state - blue
              strokeColor = 0x4a90e2;
              fillColor = 0x87ceeb;
              strokeWidth = 3;
            } else if (isSelected) {
              // Selected state - orange
              strokeColor = 0xff6b35;
              fillColor = 0xffd700;
              strokeWidth = 3;
            } else {
              // Default state - brown (like a wooden door)
              strokeColor = 0x8B4513;
              fillColor = 0xDEB887;
              strokeWidth = 2;
            }
            
            // Use shape alpha for preview mode
            const fillAlpha = shape.isPreview ? (shape.alpha || 0.5) : 0.8;
            const lineAlpha = shape.isPreview ? (shape.alpha || 0.5) : 1;
            
            // Draw quarter circle door (0-90 degrees) - hinge point at bottom-left of the door area
            g.lineStyle(strokeWidth, strokeColor, lineAlpha);
            g.beginFill(fillColor, fillAlpha);
            
            // Start from the hinge point at bottom-left and draw quarter arc
            g.moveTo(0, radius);  // Hinge point at bottom-left
            g.arc(0, radius, radius, -Math.PI / 2, 0); // Arc from top to right
            g.lineTo(0, radius); // Close back to hinge point
            g.endFill();
            
            // Draw the door edge line (the wall-contact line)
            g.lineStyle(strokeWidth + 1, strokeColor, lineAlpha);
            g.moveTo(0, 0);       // From top of wall contact
            g.lineTo(0, radius);  // To hinge point
            
            // Draw hinge point
            g.lineStyle(0);
            g.beginFill(0x444444, lineAlpha);
            g.drawCircle(0, radius, radius * 0.08);  // Hinge at bottom-left
            g.endFill();
          }}
        />
      </Container>

      {/* Door labels removed per user request */}

      {/* Attachment status indicator */}
      {shape.isAttached && shape.attachedTo && (
        <Graphics
          x={shape.x + radius + 15}
          y={shape.y + radius / 2}
          draw={(g) => {
            g.clear();
            
            // Connection status badge
            g.lineStyle(2, 0x4a90e2, 1);
            g.beginFill(0x4a90e2, 0.8);
            g.drawCircle(0, 0, 8);
            g.endFill();
            
            // Checkmark
            g.lineStyle(2, 0xffffff, 1);
            g.moveTo(-3, 0);
            g.lineTo(-1, 2);
            g.lineTo(3, -2);
          }}
        />
      )}

      {/* Door orientation indicator when selected */}
      {isSelected && !isNearRoom && (
        <Text
          text={`Mode: ${forceOutward ? 'Outward' : 'Auto'} (Press F to toggle)`}
          x={shape.x + radius}
          y={shape.y + radius + 40}
          anchor={0.5}
          style={{
            fontFamily: 'Arial',
            fontSize: 8,
            fill: 0x888888,
            align: 'center',
            fontStyle: 'italic',
          }}
        />
      )}

      {/* Debug hinge point indicator when selected */}
      {isSelected && (
        <Graphics
          draw={(g) => {
            g.clear();
            const currentRotation = shape.rotation || 0;
            const checkPoints = getDoorCheckPoints(shape.x, shape.y, radius, currentRotation);
            
            // Draw all check points with different colors
            const pointColors = {
              hinge: 0xff0000,        // Red - hinge point
              topLeft: 0x00ff00,      // Green - top left
              topRight: 0x0000ff,     // Blue - top right
              leftEdge: 0xffff00,     // Yellow - left edge
              topEdge: 0xff00ff,      // Magenta - top edge
              curveQuarter: 0x00ffff, // Cyan - curve quarter
              curveHalf: 0xffa500     // Orange - curve half
            };
            
            Object.entries(checkPoints).forEach(([pointName, point]) => {
              const color = pointColors[pointName] || 0x888888;
              const isDetectionPoint = snapTarget && snapTarget.detectionPoint === pointName;
              const isAlignmentDetection = snapTarget && snapTarget.alignmentType;
              
              // Draw point indicator - larger if it detected the wall
              g.lineStyle(isDetectionPoint ? 3 : 1, color, 1.0);
              g.beginFill(color, isDetectionPoint ? 1.0 : 0.6);
              g.drawCircle(point.x, point.y, isDetectionPoint ? 6 : 3);
              g.endFill();
              
              // Draw connection line from door center
              g.lineStyle(1, color, isDetectionPoint ? 0.8 : 0.3);
              g.moveTo(shape.x + radius / 2, shape.y + radius / 2);
              g.lineTo(point.x, point.y);
              
              // Add special indicators for alignment detection
              if (isDetectionPoint && isAlignmentDetection) {
                // Double ring for alignment detection
                g.lineStyle(2, 0xffffff, 1.0);
                g.drawCircle(point.x, point.y, 10);
                g.lineStyle(1, 0x00ff00, 0.8);
                g.drawCircle(point.x, point.y, 12);
              } else if (isDetectionPoint) {
                // Single ring for proximity detection
                g.lineStyle(1, 0xffffff, 0.8);
                g.drawCircle(point.x, point.y, 8);
              }
            });
            
            // Draw main hinge point larger for emphasis
            const hingePoint = checkPoints.hinge;
            g.lineStyle(2, 0xff0000, 1.0);
            g.beginFill(0xff0000, 0.8);
            g.drawCircle(hingePoint.x, hingePoint.y, 5);
            g.endFill();
            
            // Show alignment lines if alignment detection is active
            if (snapTarget && snapTarget.alignmentType) {
              g.lineStyle(2, 0x00ff00, 0.5);
              if (snapTarget.alignmentType === 'horizontal') {
                // Draw horizontal alignment line
                const y = snapTarget.connectionPoint.y;
                g.moveTo(shape.x - 20, y);
                g.lineTo(shape.x + radius + 20, y);
              } else if (snapTarget.alignmentType === 'vertical') {
                // Draw vertical alignment line
                const x = snapTarget.connectionPoint.x;
                g.moveTo(x, shape.y - 20);
                g.lineTo(x, shape.y + radius + 20);
              }
            }
          }}
        />
      )}

      {/* Control nodes for door scaling and rotation - positioned relative to door */}
      {isSelected && !isPartOfMergedStructure && (
        <>
          {/* Bottom line scaling handle - the primary scaling control */}
          <Graphics
            x={shape.x + radius}
            y={shape.y + radius}
            interactive={true}
            cursor="ew-resize"
            pointerdown={(e) => handleNodeMouseDown(e, 'bottom-line')}
            draw={(g) => {
              g.clear();
              g.lineStyle(2, 0xff6b35);
              g.beginFill(0xff6b35, 0.8);
              g.drawRect(-4, -4, 8, 8);
              g.endFill();
              
              // Scale indicator
              g.lineStyle(1, 0xffffff);
              g.moveTo(-2, -2);
              g.lineTo(2, -2);
              g.moveTo(-2, 0);
              g.lineTo(2, 0);
              g.moveTo(-2, 2);
              g.lineTo(2, 2);
            }}
          />

          {/* Corner nodes for general adjustment (smaller and less prominent) */}
          {[
            { x: shape.x, y: shape.y }, // top-left
            { x: shape.x + radius, y: shape.y }, // top-right
            { x: shape.x, y: shape.y + radius }, // bottom-left (hinge)
          ].map((pos, index) => (
            <CornerNode
              key={`corner-${index}`}
              x={pos.x}
              y={pos.y}
              index={index}
              shape={shape}
              onNodeMouseDown={handleNodeMouseDown}
              onNodeMouseMove={handleNodeMouseMove}
              onNodeMouseUp={handleNodeMouseUp}
              containerRef={containerRef}
            />
          ))}

          {/* Center node for moving */}
          <CenterNode
            x={shape.x + radius / 2}
            y={shape.y + radius / 2}
            shape={shape}
            onNodeMouseDown={handleNodeMouseDown}
            onNodeMouseMove={handleNodeMouseMove}
            onNodeMouseUp={handleNodeMouseUp}
            containerRef={containerRef}
          />

          {/* Rotation handle */}
          <Graphics
            x={shape.x + radius + 20}
            y={shape.y + radius / 2}
            interactive={true}
            cursor="grab"
            pointerdown={(e) => handleNodeMouseDown(e, 'rotation')}
            draw={(g) => {
              g.clear();
              g.lineStyle(2, 0x4a90e2);
              g.beginFill(0x4a90e2, 0.8);
              g.drawCircle(0, 0, 8);
              g.endFill();
              
              // Rotation icon
              g.lineStyle(2, 0xffffff);
              g.arc(0, 0, 4, 0, Math.PI * 1.5);
              g.moveTo(2, -4);
              g.lineTo(0, -6);
              g.lineTo(2, -8);
            }}
          />

          {/* Connection line to rotation handle */}
          <Graphics
            draw={(g) => {
              g.clear();
              g.lineStyle(1, 0x4a90e2, 0.5);
              g.moveTo(shape.x + radius, shape.y + radius / 2);
              g.lineTo(shape.x + radius + 20, shape.y + radius / 2);
            }}
          />

          {/* Enhanced Flip/Orientation toggle buttons */}
          <Graphics
            x={shape.x + radius / 2}
            y={shape.y - 20}
            interactive={true}
            cursor="pointer"
            pointerdown={() => {
              // Smart flip - rotates 180° but maintains wall attachment if attached
              const currentRotation = shape.rotation || 0;
              const newRotation = currentRotation + Math.PI;
              
              const updates = { rotation: newRotation };
              
              // If attached, maintain the attachment but update the orientation
              if (shape.isAttached && shape.attachedTo) {
                console.log('🔄 FLIPPING ATTACHED DOOR:', {
                  doorId: shape.id,
                  oldRotation: currentRotation,
                  newRotation: newRotation,
                  attachedTo: shape.attachedTo.side
                });
                // Keep attachment but update rotation
                updates.attachedTo = { ...shape.attachedTo };
              }
              
              onUpdate(updates);
              console.log('🚪 DOOR FLIPPED:', currentRotation, '→', newRotation);
            }}
            draw={(g) => {
              g.clear();
              g.lineStyle(2, 0x4a90e2);
              g.beginFill(0x4a90e2, 0.8);
              g.drawRoundedRect(-20, -8, 40, 16, 8);
              g.endFill();
              
              // Enhanced flip icon with directional arrows
              g.lineStyle(2, 0xffffff);
              g.moveTo(-10, -3);
              g.lineTo(-5, 0);
              g.lineTo(-10, 3);
              g.moveTo(10, -3);
              g.lineTo(5, 0);
              g.lineTo(10, 3);
              
              // Center rotation symbol
              g.lineStyle(1, 0xffffff);
              g.drawCircle(0, 0, 2);
            }}
          />

          {/* Enhanced Flip label */}
          <Text
            text="Flip Door"
            x={shape.x + radius / 2}
            y={shape.y - 25}
            anchor={0.5}
            style={{
              fontFamily: 'Arial',
              fontSize: 9,
              fill: 0x4a90e2,
              align: 'center',
              fontWeight: 'bold',
            }}
          />

          {/* Quarter Turn button for fine orientation control */}
          <Graphics
            x={shape.x + radius / 2 + 50}
            y={shape.y - 20}
            interactive={true}
            cursor="pointer"
            pointerdown={() => {
              // Quarter turn (90°) for fine adjustment
              const currentRotation = shape.rotation || 0;
              const newRotation = currentRotation + Math.PI / 2;
              
              const updates = { rotation: newRotation };
              
              // If attached, maintain the attachment
              if (shape.isAttached && shape.attachedTo) {
                console.log('🔄 QUARTER TURN ATTACHED DOOR:', {
                  doorId: shape.id,
                  oldRotation: currentRotation,
                  newRotation: newRotation,
                  attachedTo: shape.attachedTo.side
                });
                updates.attachedTo = { ...shape.attachedTo };
              }
              
              onUpdate(updates);
              console.log('🚪 DOOR QUARTER TURNED:', currentRotation, '→', newRotation);
            }}
            draw={(g) => {
              g.clear();
              g.lineStyle(2, 0x2ecc71);
              g.beginFill(0x2ecc71, 0.8);
              g.drawRoundedRect(-15, -8, 30, 16, 8);
              g.endFill();
              
              // Quarter turn icon (90° arrow)
              g.lineStyle(2, 0xffffff);
              g.arc(0, 0, 4, 0, Math.PI / 2);
              g.moveTo(4, 0);
              g.lineTo(2, -2);
              g.moveTo(4, 0);
              g.lineTo(2, 2);
            }}
          />

          {/* Quarter Turn label */}
          <Text
            text="90°"
            x={shape.x + radius / 2 + 50}
            y={shape.y - 25}
            anchor={0.5}
            style={{
              fontFamily: 'Arial',
              fontSize: 9,
              fill: 0x2ecc71,
              align: 'center',
              fontWeight: 'bold',
            }}
          />
        </>
      )}
    </Container>
  );
};

export default Door; 
